import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  DEFAULT_DRUM_BASE_NOTE,
  DEFAULT_DRUM_SAMPLE_AMP_ENV,
  DEFAULT_SLOT_NAMES,
  DRUM_PAD_COUNT,
  cloneDrumKit,
  normalizeDrumKit,
  normalizeDrumKitMode,
  type DrumKit,
  type DrumKitMode,
  type DrumPad,
  type InstrumentSlot,
} from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import { getLabActions } from "../labActions";
import { assignSamplesToDrumPads, clearDrumPadSample } from "../lib/drumPadAssign";
import { listImportedSampleAssets } from "../lib/sampleLibrary";
import { projectStore } from "../store/projectStore";
import { patchUi, uiStore } from "../store/uiStore";
import { DrumPadGrid } from "./DrumPadGrid";
import { formatOctaveLabel, MusicalKeyboard } from "./MusicalKeyboard";
import { ParamKnob } from "./ParamKnob";
import { instrumentPresetLearnPath } from "../controls/instrumentPresetLeaves";

const STORAGE_KEY = "audio-lab:drum-author-modal";
const PANEL_WIDTH = 980;
const PANEL_MIN_HEIGHT = 720;

type PanelPos = { left: number; top: number };

/**
 * Floating drum author: pad grid, knobs, kit bank, keyboard.
 */
export function DrumAuthorModal() {
  const open = useStoreSelector(uiStore, s => s.authorOpen);
  const instrumentSlot = useStoreSelector(uiStore, s => s.instrumentSlot);
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const drumBank = useStoreSelector(projectStore, s => s.drumBank);

  const slot = (instrumentSlot ?? 0) as InstrumentSlot;
  const instrument = instruments[slot];
  const isDrums = instrument?.kind === "drums";
  const kit = isDrums ? normalizeDrumKit(instrument.kit) : null;

  const panelRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | undefined>();
  const [pos, setPos] = useState<PanelPos>(() => loadPos() ?? { left: 12, top: 12 });
  const [octaveShift, setOctaveShift] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [selectedPad, setSelectedPad] = useState(0);
  const [sampleNames, setSampleNames] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (!open || !isDrums || !kit) {
      return;
    }
    if (normalizeDrumKitMode(kit.mode) !== "sample") {
      return;
    }
    void listImportedSampleAssets().then(assets => {
      const map = new Map<string, string>();
      for (const a of assets) {
        map.set(a.id, a.name);
      }
      setSampleNames(map);
    });
    void getLabActions()?.ensureDrumSamples?.(slot);
  }, [open, isDrums, slot, kit?.mode, kit?.pads.map(p => p.sampleId).join("|")]);

  useEffect(() => {
    if (!open || !isDrums) {
      return;
    }
    setPos(prev => {
      const el = panelRef.current;
      const width = el?.offsetWidth || PANEL_WIDTH;
      const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(loadPos() ?? defaultPos(), width, height);
      savePos(next);
      return next.left === prev.left && next.top === prev.top ? prev : next;
    });
    panelRef.current?.focus();
  }, [open, isDrums]);

  useEffect(() => {
    if (!open || !isDrums) {
      return;
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }
    function onResize(): void {
      setPos(prev => {
        const el = panelRef.current;
        const width = el?.offsetWidth || PANEL_WIDTH;
        const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
        const next = clampPos(prev, width, height);
        savePos(next);
        return next;
      });
    }
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", onResize);
      dragCleanupRef.current?.();
      dragCleanupRef.current = undefined;
    };
  }, [open, isDrums]);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    setOctaveShift(0);
    setSelectedPad(0);
  }, [open, slot]);

  function close(): void {
    dragCleanupRef.current?.();
    dragCleanupRef.current = undefined;
    getLabActions()?.closeInstrumentAuthor?.();
    patchUi({ authorOpen: false });
  }

  function beginDrag(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest("button") || target?.closest("input") || target?.closest("select")) {
      return;
    }
    event.preventDefault();
    const el = panelRef.current;
    if (!el) {
      return;
    }
    dragCleanupRef.current?.();
    const rect = el.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    setDragging(true);

    const onMove = (moveEvent: PointerEvent): void => {
      const width = el.offsetWidth || PANEL_WIDTH;
      const height = el.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(
        {
          left: moveEvent.clientX - offsetX,
          top: moveEvent.clientY - offsetY,
        },
        width,
        height,
      );
      savePos(next);
      setPos(next);
    };

    const onUp = (): void => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = undefined;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = onUp;
  }

  function commit(next: DrumKit): void {
    getLabActions()?.updateInstrumentKit?.(slot, next);
  }

  function patchKit(partial: Partial<DrumKit>): void {
    if (!kit) {
      return;
    }
    commit(normalizeDrumKit({ ...cloneDrumKit(kit), ...partial }));
  }

  function patchPad(index: number, partial: Partial<DrumPad>): void {
    if (!kit) {
      return;
    }
    const pads = cloneDrumKit(kit).pads;
    pads[index] = { ...pads[index]!, ...partial };
    commit(normalizeDrumKit({ ...kit, pads }));
  }

  function auditionPad(index: number): void {
    if (!kit) {
      return;
    }
    getLabActions()?.instrumentNoteOn?.(slot, kit.baseNote + index, 0.9);
  }

  function setMode(mode: DrumKitMode): void {
    patchKit({ mode });
    if (mode === "sample") {
      void getLabActions()?.ensureDrumSamples?.(slot);
    }
  }

  async function assignOsFiles(startIndex: number, files: File[]): Promise<void> {
    if (!kit) {
      return;
    }
    const metas = await getLabActions()?.importLibrarySamples?.(files) ?? [];
    if (metas.length === 0) {
      return;
    }
    commit(assignSamplesToDrumPads(
      kit,
      startIndex,
      metas.map(m => ({ id: m.id, name: m.name })),
    ));
    void getLabActions()?.ensureDrumSamples?.(slot);
  }

  if (!open || !isDrums || !kit) {
    return null;
  }

  const pad = kit.pads[selectedPad] ?? kit.pads[0]!;
  const sampleMode = normalizeDrumKitMode(kit.mode) === "sample";
  const sampleMissing = sampleMode && Boolean(pad.sampleId) && !sampleNames.has(pad.sampleId!);
  const slotLabel = DEFAULT_SLOT_NAMES[slot] ?? `I${slot + 1}`;
  const title = `Drum author · ${kit.name} · ${slotLabel}`;

  return createPortal(
    <div
      ref={panelRef}
      className={`synth-author fixed z-[55] flex h-[min(820px,calc(100vh-24px))] w-[min(980px,calc(100vw-24px))] max-[720px]:h-[calc(100vh-16px)] max-[720px]:w-[calc(100vw-16px)] flex-col overflow-hidden rounded-lg border border-lab-line bg-lab-panel text-xs text-slate-300 shadow-[0_28px_64px_rgba(0,0,0,0.5)] outline-none ${dragging ? "select-none" : ""}`}
      tabIndex={0}
      role="dialog"
      aria-modal="false"
      aria-label="Drum author"
      style={{ left: pos.left, top: pos.top }}
    >
      <div
        className="synth-author-header flex shrink-0 cursor-move touch-none select-none items-center justify-between gap-2 border-b border-white/8 bg-lab-panel-bar px-[0.7rem] py-2"
        onPointerDown={beginDrag}
      >
        <span className="synth-author-title min-w-0 truncate text-xs font-semibold text-slate-100">
          {title}
        </span>
        <button
          type="button"
          className="rounded border border-lab-line bg-transparent px-2 py-0.5 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={close}
        >
          Close
        </button>
      </div>

      <div className="synth-author-tabs flex shrink-0 flex-wrap gap-[0.25rem] border-b border-white/[0.06] bg-lab-panel-bar px-2 py-[0.35rem]">
        {Array.from({ length: instruments.length }, (_, i) => {
          const s = i as InstrumentSlot;
          const label = DEFAULT_SLOT_NAMES[s] ?? `I${i + 1}`;
          const selected = slot === s;
          const tone = selected
            ? "border-lab-accent bg-[rgba(61,139,253,0.22)] text-white"
            : "border-lab-line bg-transparent text-lab-muted hover:bg-white/[0.06] hover:text-slate-200";
          return (
            <button
              key={s}
              type="button"
              className={`min-w-[2.8rem] truncate rounded border px-2 py-[0.28rem] text-[10px] ${tone}`}
              onClick={() => getLabActions()?.setInstrumentSlot?.(s)}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="synth-author-browser flex shrink-0 flex-wrap items-center gap-2 border-b border-white/[0.06] px-3 py-2">
        <div className="flex shrink-0 overflow-hidden rounded border border-lab-line">
          <button
            type="button"
            className={`px-2 py-1 text-[11px] ${!sampleMode ? "bg-[rgba(61,139,253,0.28)] text-white" : "text-lab-muted hover:bg-white/[0.06]"}`}
            aria-pressed={!sampleMode}
            onClick={() => setMode("synth")}
          >
            Synth
          </button>
          <button
            type="button"
            className={`px-2 py-1 text-[11px] ${sampleMode ? "bg-[rgba(61,139,253,0.28)] text-white" : "text-lab-muted hover:bg-white/[0.06]"}`}
            aria-pressed={sampleMode}
            onClick={() => setMode("sample")}
          >
            Sample
          </button>
        </div>
        <select
          className="min-w-0 grow basis-[10rem] rounded border border-lab-line bg-lab-panel px-2 py-1 text-[11px] text-slate-200"
          value=""
          aria-label="Load drum kit"
          onChange={event => {
            const id = event.target.value;
            if (id) {
              getLabActions()?.applyDrumBankEntry?.(slot, id);
            }
          }}
        >
          <option value="">Load kit…</option>
          {drumBank.map(entry => (
            <option key={entry.id} value={entry.id}>{entry.name}</option>
          ))}
        </select>
        <button
          type="button"
          className="rounded border border-lab-line bg-transparent px-2 py-1 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={() => getLabActions()?.saveDrumBank?.(slot)}
        >
          Save
        </button>
        <button
          type="button"
          className="rounded border border-lab-line bg-transparent px-2 py-1 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={() => getLabActions()?.saveDrumBankAs?.(slot)}
        >
          Save as…
        </button>
        <button
          type="button"
          className="rounded border border-lab-line bg-transparent px-2 py-1 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={() => getLabActions()?.resetInstrumentSlot?.(slot)}
        >
          Reset kit
        </button>
      </div>

      <div className="synth-author-scroll min-h-0 flex-1 overflow-auto px-3 py-3">
        <label className="mb-3 flex items-center gap-2 text-[11px] text-lab-muted">
          <span className="w-14 shrink-0">Name</span>
          <input
            className="min-w-0 grow rounded border border-lab-line bg-lab-panel px-2 py-1 text-[12px] text-slate-200"
            value={kit.name}
            onChange={event => patchKit({ name: event.target.value })}
          />
        </label>

        {sampleMode && (
          <p className="mb-2 text-[10px] text-lab-muted">
            Drop library samples or .wav files onto pads to assign oneshots.
          </p>
        )}

        <DrumPadGrid
          className="mb-4"
          kit={kit}
          selectedPad={selectedPad}
          onSelectPad={setSelectedPad}
          onAuditionPad={auditionPad}
          onAssignLibrarySamples={(start, samples) => {
            commit(assignSamplesToDrumPads(kit, start, samples));
            void getLabActions()?.ensureDrumSamples?.(slot);
          }}
          onAssignOsFiles={(start, files) => void assignOsFiles(start, files)}
        />

        <div className="mb-3 grid grid-cols-1 gap-[0.55rem] sm:grid-cols-[repeat(auto-fit,minmax(240px,1fr))]">
          <section className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]">
            <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
              Pad {selectedPad + 1} · MIDI {kit.baseNote + selectedPad}
              {kit.baseNote === DEFAULT_DRUM_BASE_NOTE ? " (C4)" : ""} · {DRUM_PAD_COUNT} pads
              {sampleMode ? " · Sample" : " · Synth"}
            </h3>
            <label className="mb-2 flex items-center gap-2 text-[11px] text-lab-muted">
              <span className="w-14 shrink-0">Name</span>
              <input
                className="min-w-0 grow rounded border border-lab-line bg-lab-panel px-2 py-1 text-[12px] text-slate-200"
                value={pad.name}
                onChange={event => patchPad(selectedPad, { name: event.target.value })}
              />
            </label>
            {sampleMode && (
              <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-300">
                <span className="text-lab-muted">Sample</span>
                <span className="min-w-0 truncate">
                  {pad.sampleId
                    ? (sampleNames.get(pad.sampleId) ?? pad.sampleId)
                    : "— empty —"}
                </span>
                {pad.sampleId ? (
                  <button
                    type="button"
                    className="rounded border border-lab-line px-1.5 py-0.5 text-[10px] text-lab-muted hover:bg-white/[0.06]"
                    onClick={() => commit(clearDrumPadSample(kit, selectedPad))}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            )}
            {sampleMissing && (
              <p className="m-0 text-[11px] text-rose-200">
                Sample missing — re-import or drop another onto this pad.
              </p>
            )}
          </section>

          {sampleMode ? (
            <>
              <section className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]">
                <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                  Oneshot
                </h3>
                <div className="flex flex-wrap items-start gap-x-[0.7rem] gap-y-[0.55rem]">
                  <ParamKnob label="Level" min={0} max={1} step={0.01} value={pad.level} defaultValue={0.85} learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.level`)} onChange={v => patchPad(selectedPad, { level: v })} />
                  <ParamKnob label="Pitch" min={-24} max={24} step={1} value={pad.pitchSemis ?? 0} defaultValue={0} format={v => `${v >= 0 ? "+" : ""}${Math.round(v)} st`} learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.pitchSemis`)} onChange={v => patchPad(selectedPad, { pitchSemis: v })} />
                </div>
              </section>
              <section className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]">
                <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                  Amp
                </h3>
                <div className="flex flex-wrap items-start gap-x-[0.7rem] gap-y-[0.55rem]">
                  {(["a", "d", "s", "r"] as const).map(key => {
                    const env = pad.ampEnv ?? DEFAULT_DRUM_SAMPLE_AMP_ENV;
                    const isSustain = key === "s";
                    return (
                      <ParamKnob
                        key={key}
                        label={key.toUpperCase()}
                        min={isSustain ? 0 : 0.001}
                        max={isSustain ? 1 : 2}
                        step={isSustain ? 0.01 : 0.001}
                        value={env[key]}
                        defaultValue={DEFAULT_DRUM_SAMPLE_AMP_ENV[key]}
                        format={v => (isSustain ? v.toFixed(2) : `${Math.round(v * 1000)} ms`)}
                        learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.ampEnv.${key}`)}
                        onChange={v =>
                          patchPad(selectedPad, {
                            ampEnv: { ...env, [key]: v },
                          })
                        }
                      />
                    );
                  })}
                </div>
              </section>
            </>
          ) : (
            <>
              <section className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]">
                <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                  Body
                </h3>
                <div className="flex flex-wrap items-start gap-x-[0.7rem] gap-y-[0.55rem]">
                  <ParamKnob label="Level" min={0} max={1} step={0.01} value={pad.level} defaultValue={0.85} learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.level`)} onChange={v => patchPad(selectedPad, { level: v })} />
                  <ParamKnob label="Pitch" min={20} max={8000} step={1} value={pad.pitchHz} defaultValue={120} format={v => `${Math.round(v)} Hz`} learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.pitchHz`)} onChange={v => patchPad(selectedPad, { pitchHz: v })} />
                  <ParamKnob label="P.Env" min={0} max={4} step={0.05} value={pad.pitchEnv} defaultValue={1.2} learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.pitchEnv`)} onChange={v => patchPad(selectedPad, { pitchEnv: v })} />
                  <ParamKnob label="Decay" min={10} max={2000} step={5} value={pad.decayMs} defaultValue={180} format={v => `${Math.round(v)} ms`} learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.decayMs`)} onChange={v => patchPad(selectedPad, { decayMs: v })} />
                </div>
              </section>
              <section className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]">
                <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                  Layers
                </h3>
                <div className="flex flex-wrap items-start gap-x-[0.7rem] gap-y-[0.55rem]">
                  <ParamKnob label="Tone" min={0} max={1} step={0.01} value={pad.toneLevel} defaultValue={0.7} learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.toneLevel`)} onChange={v => patchPad(selectedPad, { toneLevel: v })} />
                  <ParamKnob label="Noise" min={0} max={1} step={0.01} value={pad.noiseLevel} defaultValue={0.15} learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.noiseLevel`)} onChange={v => patchPad(selectedPad, { noiseLevel: v })} />
                  <ParamKnob label="Click" min={0} max={1} step={0.01} value={pad.clickLevel} defaultValue={0.25} learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.clickLevel`)} onChange={v => patchPad(selectedPad, { clickLevel: v })} />
                  <ParamKnob label="Filter" min={200} max={18000} step={10} value={pad.filterHz} defaultValue={6000} format={v => `${Math.round(v)} Hz`} learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.filterHz`)} onChange={v => patchPad(selectedPad, { filterHz: v })} />
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      <div className="synth-author-keys shrink-0 border-t border-white/[0.06] bg-lab-panel-bar px-3 py-2">
        <div className="mb-1 flex items-center justify-between text-[10px] text-lab-muted">
          <span>Audition · {formatOctaveLabel(octaveShift)}</span>
        </div>
        <MusicalKeyboard
          key={slot}
          focusHostRef={panelRef}
          baseMidi={kit.baseNote}
          octaves={2}
          voiceMode="poly"
          onNoteOn={(note, velocity) => getLabActions()?.instrumentNoteOn?.(slot, note, velocity)}
          onNoteOff={note => getLabActions()?.instrumentNoteOff?.(slot, note)}
          onOctaveChange={setOctaveShift}
        />
      </div>
    </div>,
    document.body,
  );
}

function loadPos(): PanelPos | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<PanelPos>;
    if (typeof parsed.left === "number" && typeof parsed.top === "number") {
      return { left: parsed.left, top: parsed.top };
    }
  } catch {
    // ignore
  }
  return undefined;
}

function savePos(pos: PanelPos): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // ignore
  }
}

function defaultPos(): PanelPos {
  const left = Math.max(12, (window.innerWidth - PANEL_WIDTH) / 2);
  const top = Math.max(12, Math.min(48, window.innerHeight * 0.06));
  return { left, top };
}

function clampPos(pos: PanelPos, width: number, height: number): PanelPos {
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const maxTop = Math.max(
    8,
    window.innerHeight - Math.min(height, window.innerHeight - 16) - 8,
  );
  return {
    left: Math.min(maxLeft, Math.max(8, pos.left)),
    top: Math.min(maxTop, Math.max(8, pos.top)),
  };
}
